import { useCallback, useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Container, Header } from "components/Page"
import { deleteMarketUser, fetchMarketUsers } from "api/users"
import { queryCache, useMutation, useQuery } from "react-query"

import Breadcrumbs from "components/Breadcrumbs"
import Button from "ui/buttons/Button"
import ManageMember from "./ManageMember"
import { TeamMembers } from "../TeamMembers"
import { MarketTeam } from "../TeamMembers/types"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useToggle } from "hooks/useToggle"
import { ApiError } from "api/http/utils"
import { useSendFeedBack } from "components/FeedbackSnackbar"

export const MarketTeams = () => {
  const { t } = useI18n()
  const feedback = useSendFeedBack()
  const { open, onOpen, onClose } = useToggle()
  const [member, setMember] = useState<MarketTeam>()
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const { hasAuthority } = useAuthenticated()

  // ** fetch teams **
  const { data, isLoading } = useQuery(["market-teams"], fetchMarketUsers)

  // ** delete team **
  const [deleteTeam] = useMutation(deleteMarketUser, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("market-teams")
      queryCache.invalidateQueries("unassigned-users")
    },
    onError: (e: ApiError) => {
      const error = e as ApiError
      const errorMap = error?.errorsMap ?? {}
      feedback(t(errorMap.users) || t("teams.delete.error"), "error")
      setModalInfo(undefined)
    },
  })

  const onEdit = (id: string) => {
    const currentMember = data?.find((m) => m?.id === id)
    setMember(currentMember)
    onOpen()
  }

  const resetMember = () => setMember(undefined)

  const onDelete = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("teams.member.list.delete.title"),
        type: "primary_destructive",
        description: t("teams.member.list.delete.description"),
        submitLabel: t("teams.member.list.delete.action"),
        onSubmit: async () => await deleteTeam(id),
      })
    },
    [deleteTeam, t],
  )
  const isModalOpen = Boolean(modalInfo)

  return (
    <Container>
      <Breadcrumbs section={t("nav.admin")} current={t("nav.market.team")} />
      <Header label={t("nav.market.team")}>
        {hasAuthority(["AUTH_CREATE_USER_MARKET"]) && (
          <Button variant="contained" color="primary" onClick={onOpen}>
            {t("member.create.button")}
          </Button>
        )}
      </Header>
      <TeamMembers
        data={data}
        isLoading={isLoading}
        onDelete={onDelete}
        onEdit={onEdit}
        hasColumnsReordering={false}
        onClose={onClose}
        newMember={onOpen}
      />
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
      <ManageMember
        open={open}
        onClose={onClose}
        member={member}
        resetMember={resetMember}
      />
    </Container>
  )
}
